/**
 * Error classifier for transforming raw backend / network failures into
 * user-facing copy plus an optional next-step action.
 *
 * The backend returns a mix of structured error payloads (axios `error.response`)
 * and bare `Error` objects. This helper inspects whatever it gets and bins it
 * into one of a small set of `kind`s so the UI can:
 *  - show a humane sentence rather than a stack trace,
 *  - point at the next thing the user should do (re-auth, retry, open broker app),
 *  - decide whether the alert should be `error` (action required) or `warning`
 *    (transient / will retry on its own).
 *
 * Important: do NOT leak secrets, account balances, stack traces, or Java
 * exception class names into the user-facing strings produced here.
 */
export type ErrorKind =
    | 'data-missing'
    | 'auth'
    | 'rate-limit'
    | 'broker'
    | 'network'
    | 'server'
    | 'unknown';

export interface SuggestedAction {
    label: string;
    /** Optional href; if present the consumer can render an anchor or `Button` with `href`. */
    href?: string;
    /** Optional onClick; consumer is responsible for wiring it (e.g. retry the failed call). */
    onClick?: () => void;
}

export interface ClassifiedError {
    kind: ErrorKind;
    userMessage: string;
    suggestedAction?: SuggestedAction;
}

interface ClassifyOptions {
    /** Optional retry hook the caller wires up (e.g. re-issue the failed fetch). */
    onRetry?: () => void;
}

/** Heuristic: does this string look like a Java/Spring stack trace line? */
export function looksLikeStackTrace(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    return (
        /\bat\s+java\./.test(value) ||
        /\bat\s+com\.juhsis\./.test(value) ||
        /\bat\s+org\.springframework\./.test(value) ||
        /\bat\s+jdk\.internal\./.test(value) ||
        /\bat\s+sun\./.test(value)
    );
}

/**
 * Extract a best-effort message string and HTTP status from whatever the caller
 * hands us: an axios error, a fetch Response error, a thrown Error, or a raw
 * payload object pulled out of `error.response.data`.
 */
function extractParts(error: unknown): { message: string; status?: number; payload?: Record<string, unknown> } {
    if (!error) return { message: '' };

    // Axios-style: { response: { status, data: { message, ... } }, message }
    if (typeof error === 'object') {
        const e = error as {
            response?: { status?: number; data?: unknown };
            message?: string;
            code?: string;
        };
        const response = e.response;
        const data = response?.data;
        const dataObj = (data && typeof data === 'object') ? (data as Record<string, unknown>) : undefined;
        const messageFromData = typeof dataObj?.message === 'string' ? (dataObj.message as string) : '';
        const messageFromError = typeof e.message === 'string' ? e.message : '';
        const codeFromError = typeof e.code === 'string' ? e.code : '';
        return {
            message: messageFromData || messageFromError || codeFromError || '',
            status: typeof response?.status === 'number' ? response.status : undefined,
            payload: dataObj,
        };
    }

    if (typeof error === 'string') return { message: error };

    return { message: '' };
}

/**
 * Classify a raw error payload (and/or message string) into a user-facing
 * description with an optional suggested action.
 */
export function classifyError(error: unknown, options: ClassifyOptions = {}): ClassifiedError {
    const { message, status, payload } = extractParts(error);
    const lower = (message || '').toLowerCase();
    const exceptionClass = typeof payload?.exceptionClass === 'string' ? (payload.exceptionClass as string).toLowerCase() : '';

    // Auth / session-expired patterns. Includes upstream-broker cookie expiry,
    // which often surfaces as a 401 from our service even though Cognito is fine.
    if (
        status === 401 ||
        status === 403 ||
        lower.includes('unauthorized') ||
        lower.includes('forbidden') ||
        lower.includes('cookie expired') ||
        lower.includes('session expired') ||
        lower.includes('invalid token')
    ) {
        return {
            kind: 'auth',
            userMessage: 'Credentials expired. Sign in to the broker again or update the credential.',
            suggestedAction: {
                label: 'Open credentials',
                href: '/credentials',
            },
        };
    }

    // Rate-limit. Both upstream (SeekingAlpha / Trading212) and our own throttle.
    if (
        status === 429 ||
        lower.includes('rate limit') ||
        lower.includes('too many requests') ||
        lower.includes('throttle')
    ) {
        return {
            kind: 'rate-limit',
            userMessage: 'Hit a rate limit on the upstream service. The next scheduled run will retry.',
            suggestedAction: options.onRetry
                ? { label: 'Retry now', onClick: options.onRetry }
                : undefined,
        };
    }

    // Data-missing — most common cause of "the job ran but did nothing useful":
    // a NullPointerException coming back from the trading service when an
    // expected field wasn't populated. Treat as transient + warning, not error.
    if (
        lower.includes('nullpointerexception') ||
        exceptionClass.includes('nullpointerexception') ||
        / is null\b/.test(lower) ||
        /\bcannot invoke .* because .* is null\b/i.test(message || '')
    ) {
        return {
            kind: 'data-missing',
            userMessage: 'The job ran but expected fields were missing. It will retry on the next schedule.',
        };
    }

    // Broker-specific rejections. Trading212 wraps these in a structured error
    // payload but the human-facing message is the same: "the broker said no."
    if (
        lower.includes('trading212') ||
        lower.includes('trading 212') ||
        lower.includes('order rejected') ||
        lower.includes('order rejection') ||
        lower.includes('insufficient funds') ||
        lower.includes('broker rejected') ||
        (typeof payload?.apiErrorMessage === 'string' && (payload.apiErrorMessage as string).length > 0)
    ) {
        return {
            kind: 'broker',
            userMessage: 'Broker rejected the order. Check the broker app for details.',
            suggestedAction: options.onRetry
                ? { label: 'Retry', onClick: options.onRetry }
                : undefined,
        };
    }

    // Network / connectivity. Includes axios `ERR_NETWORK` / `ECONNREFUSED`
    // and the generic `TypeError: Failed to fetch` from the browser fetch API.
    if (
        lower.includes('network error') ||
        lower.includes('failed to fetch') ||
        lower.includes('econnrefused') ||
        lower.includes('econnreset') ||
        lower.includes('econnaborted') ||
        lower.includes('err_network') ||
        lower.includes('timeout')
    ) {
        return {
            kind: 'network',
            userMessage: "Couldn't reach the server. Try again.",
            suggestedAction: options.onRetry
                ? { label: 'Retry', onClick: options.onRetry }
                : undefined,
        };
    }

    // 5xx or anything that smells like our service crashing.
    if (
        (typeof status === 'number' && status >= 500 && status < 600) ||
        lower.includes('internal server error') ||
        lower.includes('service unavailable') ||
        lower.includes('bad gateway') ||
        lower.includes('gateway timeout')
    ) {
        return {
            kind: 'server',
            userMessage: 'The service is having trouble. Try again in a moment.',
            suggestedAction: options.onRetry
                ? { label: 'Retry', onClick: options.onRetry }
                : undefined,
        };
    }

    return {
        kind: 'unknown',
        userMessage: 'Something went wrong. Open the technical details below or retry.',
        suggestedAction: options.onRetry
            ? { label: 'Retry', onClick: options.onRetry }
            : undefined,
    };
}

/**
 * Map a classified error `kind` to the Cloudscape `Alert` type. Transient
 * conditions (data-missing, rate-limit) get `warning`; everything else `error`.
 */
export function alertTypeForKind(kind: ErrorKind): 'warning' | 'error' {
    if (kind === 'data-missing' || kind === 'rate-limit') return 'warning';
    return 'error';
}

/**
 * Recursively scrub a payload for the job-detail technical details section.
 *
 * Strips:
 *  - any key that names a balance/cash/funds field (case-insensitive
 *    suffix match against `Cash`, `Balance`, `Funds`, plus a hard-coded list
 *    of known offenders),
 *  - `exceptionClass`, `exceptionMessage`, and `stackTrace`,
 *  - any string value that looks like a Java/Spring stack trace line.
 *
 * Arrays are mapped element-wise; primitives pass through unless they look
 * like a stack trace, in which case they're replaced with `'[redacted]'`.
 */
export function sanitizeTechnicalDetails(input: unknown): unknown {
    const SENSITIVE_KEYS = new Set([
        'cfdAvailableCash',
        'isaAvailableCash',
        'freeCash',
        'availableCash',
        'balance',
        'exceptionClass',
        'exceptionMessage',
        'stackTrace',
        'stacktrace',
        'cause',
        'suppressed',
    ]);
    const SENSITIVE_SUFFIXES = ['cash', 'balance', 'funds'];

    const isSensitiveKey = (key: string): boolean => {
        if (SENSITIVE_KEYS.has(key)) return true;
        const lower = key.toLowerCase();
        return SENSITIVE_SUFFIXES.some((suffix) => lower.endsWith(suffix));
    };

    const walk = (value: unknown): unknown => {
        if (value === null || value === undefined) return value;
        if (typeof value === 'string') {
            return looksLikeStackTrace(value) ? '[redacted]' : value;
        }
        if (Array.isArray(value)) {
            return value.map((entry) => walk(entry)).filter((entry) => entry !== undefined);
        }
        if (typeof value === 'object') {
            const out: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
                if (isSensitiveKey(k)) continue;
                if (typeof v === 'string' && looksLikeStackTrace(v)) continue;
                out[k] = walk(v);
            }
            return out;
        }
        return value;
    };

    return walk(input);
}
